/**
 * Store Locator - Universal JavaScript
 * Supports both Mapbox and Google Maps Embed
 * Auto-extracts coordinates from Google Maps URLs
 */
(function () {
    'use strict';

    // Track selected category filter
    let selectedCategory = 'all';
    /**
     * Extract coordinates from various Google Maps URL formats
     * Supports: share links, place links, search links, embed URLs, short links
     */
    function extractCoordsFromGoogleUrl(url) {
        if (!url) return null;

        // Clean the URL
        url = decodeURIComponent(url);

        // Pattern 1: @lat,lng (most common in share links)
        // Example: https://www.google.com/maps/place/.../@40.7128,-74.0060,15z
        let match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 2: !3d and !4d (in embedded/some URLs)
        // Example: ...!3d40.7128!4d-74.0060
        match = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 3: !2d (lng) and !3d (lat) - Google Maps Embed format
        // Example: pb=!1m18!1m12!1m3!1d3471!2d-84.2451346!3d33.8986775
        // Note: In embed URLs, !2d is longitude and !3d is latitude
        match = url.match(/!2d(-?\d+\.?\d*)!3d(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[2]), lng: parseFloat(match[1]) };
        }

        // Pattern 4: Just !3d (lat) followed later by !2d (lng) - alternate embed format
        const latMatch = url.match(/!3d(-?\d+\.?\d*)/);
        const lngMatch = url.match(/!2d(-?\d+\.?\d*)/);
        if (latMatch && lngMatch) {
            return { lat: parseFloat(latMatch[1]), lng: parseFloat(lngMatch[1]) };
        }

        // Pattern 5: q=lat,lng (search/directions)
        // Example: https://www.google.com/maps?q=40.7128,-74.0060
        match = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 6: /place/lat,lng or search/lat,lng
        // Example: https://www.google.com/maps/place/40.7128,-74.0060
        match = url.match(/\/(place|search)\/(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[2]), lng: parseFloat(match[3]) };
        }

        // Pattern 7: center=lat,lng (embed URLs)
        match = url.match(/center=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 8: ll=lat,lng
        match = url.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 9: destination=lat,lng (directions)
        match = url.match(/destination=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Pattern 10: data=...!8m2!3dLAT!4dLNG (newer format)
        match = url.match(/!8m2!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        return null;
    }

    function init() {
        const container = document.querySelector('[data-store-locator-options]');
        if (!container) return;

        const options = JSON.parse(container.getAttribute('data-store-locator-options'));
        const mapProvider = options.mapProvider || 'google_embed';

        // Process all listings to extract coordinates from Google URLs
        processListings(options);

        if (mapProvider === 'google_embed') {
            initGoogleEmbed(options);
        } else if (mapProvider === 'mapbox') {
            // Wait for Mapbox to load (it may take longer with defer)
            waitForMapbox(() => initMapbox(options));
        }
    }

    /**
     * Wait for Mapbox library to load
     */
    function waitForMapbox(callback, attempts = 0) {
        if (typeof mapboxgl !== 'undefined') {
            callback();
        } else if (attempts < 50) { // Wait up to 5 seconds
            setTimeout(() => waitForMapbox(callback, attempts + 1), 100);
        } else {
            console.error('Store Locator: Mapbox GL JS failed to load after 5 seconds');
        }
    }

    /**
     * Process listings to extract coordinates from Google Maps URLs
     */
    function processListings(options) {
        const listings = document.querySelectorAll(`#listings${options.sid} .store-locator__item`);

        listings.forEach((item) => {
            const googleUrl = item.dataset.googleUrl;
            let lat = item.dataset.lat;
            let lng = item.dataset.lng;

            // If we have a Google URL but no coordinates, extract them
            if (googleUrl && (!lat || !lng || lat === '' || lng === '')) {
                const coords = extractCoordsFromGoogleUrl(googleUrl);
                if (coords) {
                    item.dataset.lat = coords.lat;
                    item.dataset.lng = coords.lng;
                }
            }
        });
    }

    /**
     * Google Maps Embed Provider
     */
    function initGoogleEmbed(options) {
        const iframe = document.getElementById(`google-map-embed${options.sid}`);
        const listings = document.querySelectorAll(`#listings${options.sid} .store-locator__item`);

        if (!iframe || listings.length === 0) return;

        // Always try to load first store
        const firstItem = listings[0];
        const needsUpdate = !iframe.src || iframe.src === '' || iframe.src === 'about:blank' || iframe.src.endsWith('about:blank');

        if (needsUpdate || !iframe.src.includes('google.com/maps')) {
            updateGoogleMap(iframe, firstItem, options.zoom);
        }

        listings.forEach((item, index) => {
            item.addEventListener('click', (e) => {
                // Don't trigger if clicking directions link
                if (e.target.closest('.store-locator__directions')) return;

                e.preventDefault();

                // Update active state
                listings.forEach(l => l.classList.remove('is--active'));
                item.classList.add('is--active');

                // Update map
                updateGoogleMap(iframe, item, options.zoom);
            });
        });

        // Initialize category filters
        initCategoryFilters(options.sid, listings);

        // Initialize Find Nearest Store
        initFindNearest(options.sid, listings, iframe, options.zoom);

        // Initialize Search
        initSearch(options.sid, listings, iframe, options.zoom);
    }

    /**
     * Calculate distance between two coordinates using Haversine formula
     * Returns distance in kilometers
     */
    function calculateDistance(lat1, lng1, lat2, lng2) {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Format distance for display
     */
    function formatDistance(km) {
        if (km < 1) {
            return Math.round(km * 1000) + ' m';
        } else if (km < 10) {
            return km.toFixed(1) + ' km';
        } else {
            return Math.round(km) + ' km';
        }
    }

    /**
     * Find Nearest Store functionality
     */
    function initFindNearest(sid, listings, iframe, zoom) {
        const btn = document.getElementById(`find-nearest${sid}`);
        const status = document.getElementById(`location-status${sid}`);

        if (!btn) return;

        btn.addEventListener('click', () => {
            if (!navigator.geolocation) {
                status.textContent = 'Geolocation not supported';
                status.className = 'store-locator__location-status is--error';
                return;
            }

            // Show loading state
            btn.classList.add('is--loading');
            status.textContent = 'Getting your location...';
            status.className = 'store-locator__location-status';

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userLat = position.coords.latitude;
                    const userLng = position.coords.longitude;

                    // Calculate distances and find nearest
                    let nearest = null;
                    let nearestDistance = Infinity;
                    const distances = [];

                    listings.forEach((item, index) => {
                        // Skip items hidden by category filter
                        if (item.classList.contains('is--category-hidden')) {
                            return;
                        }

                        let lat = parseFloat(item.dataset.lat);
                        let lng = parseFloat(item.dataset.lng);

                        // Try to extract from google URL if not set
                        if ((!lat || !lng) && item.dataset.googleUrl) {
                            const coords = extractCoordsFromGoogleUrl(item.dataset.googleUrl);
                            if (coords) {
                                lat = coords.lat;
                                lng = coords.lng;
                                item.dataset.lat = lat;
                                item.dataset.lng = lng;
                            }
                        }

                        if (lat && lng) {
                            const distance = calculateDistance(userLat, userLng, lat, lng);
                            distances.push({ item, index, distance });

                            if (distance < nearestDistance) {
                                nearestDistance = distance;
                                nearest = { item, index, distance };
                            }
                        }
                    });

                    // Sort distances array by distance
                    distances.sort((a, b) => a.distance - b.distance);

                    // Get the listings container
                    const listingsContainer = listings[0]?.parentElement;

                    // Remove old distance badges
                    listings.forEach(item => {
                        const existing = item.querySelector('.store-locator__distance');
                        if (existing) existing.remove();
                    });

                    // Reorder DOM elements and add distance badges
                    if (listingsContainer) {
                        distances.forEach(({ item, distance }) => {
                            // Add distance badge
                            const badge = document.createElement('span');
                            badge.className = 'store-locator__distance';
                            badge.textContent = formatDistance(distance);
                            const title = item.querySelector('.store-locator__title');
                            if (title) {
                                title.appendChild(badge);
                            }

                            // Move item to end (creates sorted order)
                            listingsContainer.appendChild(item);
                        });
                    }

                    if (nearest) {
                        // Update active state
                        listings.forEach(l => l.classList.remove('is--active'));
                        nearest.item.classList.add('is--active');

                        // Scroll to top of list (nearest is now first)
                        listingsContainer?.scrollTo({ top: 0, behavior: 'smooth' });

                        // Update map
                        updateGoogleMap(iframe, nearest.item, zoom);

                        // Update status
                        status.textContent = `Nearest: ${formatDistance(nearestDistance)} away`;
                        status.className = 'store-locator__location-status is--success';
                    }

                    btn.classList.remove('is--loading');
                },
                (error) => {
                    btn.classList.remove('is--loading');
                    let message = 'Could not get location';
                    if (error.code === 1) message = 'Location access denied';
                    if (error.code === 2) message = 'Location unavailable';
                    if (error.code === 3) message = 'Location request timed out';
                    status.textContent = message;
                    status.className = 'store-locator__location-status is--error';
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000 // Cache for 5 minutes
                }
            );
        });
    }

    /**
     * Smart Search functionality
     */
    function initSearch(sid, listings, iframe, zoom) {
        const searchInput = document.getElementById(`store-search${sid}`);
        const clearBtn = document.getElementById(`search-clear${sid}`);
        const status = document.getElementById(`location-status${sid}`);

        if (!searchInput) return;

        let debounceTimer;

        // Search handler with debounce
        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.trim().toLowerCase();

            // Show/hide clear button
            if (clearBtn) {
                clearBtn.style.display = query ? 'flex' : 'none';
            }

            // Debounce search
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                performSearch(query, listings, iframe, zoom, status);
            }, 200);
        });

        // Clear button handler
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                searchInput.value = '';
                clearBtn.style.display = 'none';
                performSearch('', listings, iframe, zoom, status);
                searchInput.focus();
            });
        }

        // Enter key handler
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const query = searchInput.value.trim().toLowerCase();
                performSearch(query, listings, iframe, zoom, status);
            }
        });
    }

    /**
     * Perform search and filter listings
     */
    function performSearch(query, listings, iframe, zoom, status) {
        let matchCount = 0;
        let firstMatch = null;

        listings.forEach((item) => {
            // Skip items hidden by category filter
            if (item.classList.contains('is--category-hidden')) {
                return;
            }

            // Get searchable text from store item
            const title = item.querySelector('.store-locator__title')?.textContent?.toLowerCase() || '';
            const details = item.querySelector('.store-locator__details')?.textContent?.toLowerCase() || '';
            const category = item.querySelector('.store-locator__category')?.textContent?.toLowerCase() || '';
            const searchText = `${title} ${details} ${category}`;

            // Check if matches query
            const matches = !query || searchText.includes(query);

            if (matches) {
                item.style.display = '';
                item.classList.remove('is--hidden');
                matchCount++;
                if (!firstMatch) {
                    firstMatch = item;
                }
            } else {
                item.style.display = 'none';
                item.classList.add('is--hidden');
            }
        });

        // Update status
        if (status) {
            if (query) {
                status.textContent = `Found ${matchCount} store${matchCount !== 1 ? 's' : ''}`;
                status.className = 'store-locator__location-status is--success';
            } else {
                status.textContent = '';
                status.className = 'store-locator__location-status';
            }
        }

        // Select first match and update map
        if (firstMatch && query) {
            listings.forEach(l => l.classList.remove('is--active'));
            firstMatch.classList.add('is--active');
            firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });

            if (iframe) {
                updateGoogleMap(iframe, firstMatch, zoom);
            }
        } else if (!query && listings.length > 0) {
            // Reset to first item when search is cleared
            const visibleItems = [...listings].filter(item => !item.classList.contains('is--hidden'));
            if (visibleItems.length > 0) {
                listings.forEach(l => l.classList.remove('is--active'));
                visibleItems[0].classList.add('is--active');
            }
        }
    }

    /**
     * Find Nearest Store functionality for Mapbox
     */
    function initFindNearestMapbox(sid, listings, map, stores) {
        const btn = document.getElementById(`find-nearest${sid}`);
        const status = document.getElementById(`location-status${sid}`);

        if (!btn) return;

        btn.addEventListener('click', () => {
            if (!navigator.geolocation) {
                status.textContent = 'Geolocation not supported';
                status.className = 'store-locator__location-status is--error';
                return;
            }

            btn.classList.add('is--loading');
            status.textContent = 'Getting your location...';
            status.className = 'store-locator__location-status';

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userLat = position.coords.latitude;
                    const userLng = position.coords.longitude;

                    let nearest = null;
                    let nearestDistance = Infinity;
                    const distances = [];

                    stores.features.forEach((feature, index) => {
                        // Skip items hidden by category filter
                        const listingItem = listings[index];
                        if (listingItem && listingItem.classList.contains('is--category-hidden')) {
                            return;
                        }

                        const coords = feature.geometry.coordinates;
                        const lat = coords[1];
                        const lng = coords[0];

                        if (lat && lng && (lat !== 0 || lng !== 0)) {
                            const distance = calculateDistance(userLat, userLng, lat, lng);
                            distances.push({ feature, index, distance, lat, lng, item: listingItem });

                            if (distance < nearestDistance) {
                                nearestDistance = distance;
                                nearest = { feature, index, distance };
                            }
                        }
                    });

                    // Sort distances array by distance
                    distances.sort((a, b) => a.distance - b.distance);

                    // Get the listings container
                    const listingsContainer = listings[0]?.parentElement;

                    // Remove old distance badges
                    listings.forEach(item => {
                        const existing = item.querySelector('.store-locator__distance');
                        if (existing) existing.remove();
                    });

                    // Reorder DOM elements and add distance badges
                    if (listingsContainer) {
                        distances.forEach(({ item, distance }) => {
                            if (item) {
                                // Add distance badge
                                const badge = document.createElement('span');
                                badge.className = 'store-locator__distance';
                                badge.textContent = formatDistance(distance);
                                const title = item.querySelector('.store-locator__title');
                                if (title) {
                                    title.appendChild(badge);
                                }

                                // Move item to end (creates sorted order)
                                listingsContainer.appendChild(item);
                            }
                        });
                    }

                    if (nearest) {
                        // Update active listing
                        listings.forEach(l => l.classList.remove('is--active'));
                        const nearestItem = distances[0]?.item;
                        if (nearestItem) {
                            nearestItem.classList.add('is--active');
                        }

                        // Scroll to top of list
                        listingsContainer?.scrollTo({ top: 0, behavior: 'smooth' });

                        // Fly to nearest store
                        map.flyTo({
                            center: nearest.feature.geometry.coordinates,
                            zoom: 15,
                            essential: true
                        });

                        status.textContent = `Nearest: ${formatDistance(nearestDistance)} away`;
                        status.className = 'store-locator__location-status is--success';
                    }

                    btn.classList.remove('is--loading');
                },
                (error) => {
                    btn.classList.remove('is--loading');
                    let message = 'Could not get location';
                    if (error.code === 1) message = 'Location access denied';
                    if (error.code === 2) message = 'Location unavailable';
                    if (error.code === 3) message = 'Location request timed out';
                    status.textContent = message;
                    status.className = 'store-locator__location-status is--error';
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000
                }
            );
        });
    }

    /**
     * Smart Search functionality for Mapbox
     */
    function initSearchMapbox(sid, listings, map, stores) {
        const searchInput = document.getElementById(`store-search${sid}`);
        const clearBtn = document.getElementById(`search-clear${sid}`);
        const status = document.getElementById(`location-status${sid}`);

        if (!searchInput) return;

        let debounceTimer;

        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.trim().toLowerCase();

            if (clearBtn) {
                clearBtn.style.display = query ? 'flex' : 'none';
            }

            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                performSearchMapbox(query, listings, map, stores, status);
            }, 200);
        });

        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                searchInput.value = '';
                clearBtn.style.display = 'none';
                performSearchMapbox('', listings, map, stores, status);
                searchInput.focus();
            });
        }

        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const query = searchInput.value.trim().toLowerCase();
                performSearchMapbox(query, listings, map, stores, status);
            }
        });
    }

    /**
     * Perform search for Mapbox
     */
    function performSearchMapbox(query, listings, map, stores, status) {
        let matchCount = 0;
        let firstMatchIndex = null;

        listings.forEach((item, index) => {
            // Skip items hidden by category filter
            if (item.classList.contains('is--category-hidden')) {
                return;
            }

            const title = item.querySelector('.store-locator__title')?.textContent?.toLowerCase() || '';
            const details = item.querySelector('.store-locator__details')?.textContent?.toLowerCase() || '';
            const category = item.querySelector('.store-locator__category')?.textContent?.toLowerCase() || '';
            const searchText = `${title} ${details} ${category}`;

            const matches = !query || searchText.includes(query);

            if (matches) {
                item.style.display = '';
                item.classList.remove('is--hidden');
                matchCount++;
                if (firstMatchIndex === null) {
                    firstMatchIndex = index;
                }
            } else {
                item.style.display = 'none';
                item.classList.add('is--hidden');
            }
        });

        if (status) {
            if (query) {
                status.textContent = `Found ${matchCount} store${matchCount !== 1 ? 's' : ''}`;
                status.className = 'store-locator__location-status is--success';
            } else {
                status.textContent = '';
                status.className = 'store-locator__location-status';
            }
        }

        if (firstMatchIndex !== null && query) {
            listings.forEach(l => l.classList.remove('is--active'));
            listings[firstMatchIndex].classList.add('is--active');
            listings[firstMatchIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Fly to first match on map
            const feature = stores.features[firstMatchIndex];
            if (feature) {
                map.flyTo({
                    center: feature.geometry.coordinates,
                    zoom: 15,
                    essential: true
                });
            }
        }
    }
    /**
     * Category Filter functionality
     */
    function initCategoryFilters(sid, listings) {
        const container = document.getElementById(`stores-locator${sid}`);
        if (!container) return;

        const filterBtns = container.querySelectorAll('.store-locator__filter-btn');
        if (filterBtns.length === 0) return;

        filterBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const category = btn.dataset.category;

                // Update global selected category
                selectedCategory = category;

                // Update active button
                filterBtns.forEach(b => b.classList.remove('is--active'));
                btn.classList.add('is--active');

                // Clear search input when changing category
                const searchInput = container.querySelector('.store-locator__search-input');
                const clearBtn = container.querySelector('.store-locator__search-clear');
                if (searchInput) {
                    searchInput.value = '';
                }
                if (clearBtn) {
                    clearBtn.style.display = 'none';
                }

                // Filter listings by category
                listings.forEach(item => {
                    const itemCategory = item.dataset.category || '';

                    if (category === 'all' || itemCategory === category) {
                        item.style.display = '';
                        item.classList.remove('is--hidden');
                        item.classList.remove('is--category-hidden');
                    } else {
                        item.style.display = 'none';
                        item.classList.add('is--hidden');
                        item.classList.add('is--category-hidden');
                    }
                });

                // Select first visible item
                const firstVisible = [...listings].find(item => !item.classList.contains('is--hidden'));
                if (firstVisible) {
                    listings.forEach(l => l.classList.remove('is--active'));
                    firstVisible.classList.add('is--active');
                    firstVisible.click();
                }
            });
        });
    }

    function updateGoogleMap(iframe, item, zoom) {
        const googleUrl = item.dataset.googleUrl;
        let lat = item.dataset.lat;
        let lng = item.dataset.lng;

        // Priority 1: If it's an embed URL, use it directly to preserve place names
        if (googleUrl && googleUrl.includes('/maps/embed')) {
            if (iframe.src !== googleUrl) {
                iframe.src = googleUrl;
            }
            return;
        }

        // Priority 2: If there's a Google URL (but not embed), try to use it as embed
        // This preserves place info when possible
        if (googleUrl && googleUrl.includes('google.com/maps')) {
            // Try to extract coordinates for fallback
            const coords = extractCoordsFromGoogleUrl(googleUrl);
            if (coords) {
                lat = coords.lat;
                lng = coords.lng;
                item.dataset.lat = lat;
                item.dataset.lng = lng;
            }
        }

        // Priority 3: Use coordinates (manual or extracted)
        if (lat && lng && lat !== '' && lng !== '') {
            const newSrc = `https://www.google.com/maps?q=${lat},${lng}&z=${zoom || 14}&output=embed`;
            if (iframe.src !== newSrc) {
                iframe.src = newSrc;
            }
        } else if (googleUrl) {
            console.warn('Store Locator: Could not load map for:', googleUrl);
        }
    }

    /**
     * Mapbox Provider
     */
    function initMapbox(options) {
        if (typeof mapboxgl === 'undefined') {
            console.error('Store Locator: Mapbox GL JS not loaded');
            return;
        }

        try {
            mapboxgl.accessToken = atob(options.accessToken);
        } catch (e) {
            console.error('Store Locator: Invalid access token');
            return;
        }

        if (!mapboxgl.accessToken || mapboxgl.accessToken === '') {
            console.error('Store Locator: Missing access token');
            return;
        }

        const storesDataEl = document.getElementById(`stores-json${options.sid}`);
        if (!storesDataEl) return;

        const stores = JSON.parse(storesDataEl.textContent);

        if (!stores.features || stores.features.length === 0) {
            console.error('Store Locator: No stores configured');
            return;
        }

        // Extract coordinates from googleUrl for stores with 0,0 coordinates
        stores.features.forEach((feature) => {
            const coords = feature.geometry.coordinates;
            const googleUrl = feature.properties.googleUrl;

            if ((coords[0] === 0 && coords[1] === 0) && googleUrl) {
                const extracted = extractCoordsFromGoogleUrl(googleUrl);
                if (extracted) {
                    feature.geometry.coordinates = [extracted.lng, extracted.lat];
                }
            }
        });

        // Filter out stores that still have 0,0 coordinates
        const validStores = {
            type: 'FeatureCollection',
            features: stores.features.filter(f =>
                f.geometry.coordinates[0] !== 0 || f.geometry.coordinates[1] !== 0
            )
        };

        if (validStores.features.length === 0) {
            console.error('Store Locator: No stores with valid coordinates');
            return;
        }

        const mapStyles = {
            streets: 'mapbox://styles/mapbox/streets-v12',
            outdoors: 'mapbox://styles/mapbox/outdoors-v12',
            light: 'mapbox://styles/mapbox/light-v11',
            dark: 'mapbox://styles/mapbox/dark-v11',
            satellite_streets: 'mapbox://styles/mapbox/satellite-streets-v12'
        };

        const map = new mapboxgl.Map({
            container: `map${options.sid}`,
            style: mapStyles[options.style] || mapStyles.streets,
            center: validStores.features[0].geometry.coordinates,
            zoom: options.zoom || 12,
            scrollZoom: options.scrollZoom || false,
            attributionControl: false,
            pitch: options.pitch || 0,
            bearing: options.bearing || 0
        });

        map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

        if (options.enableSearchBox && typeof MapboxGeocoder !== 'undefined') {
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                marker: true,
                placeholder: 'Search for a location...',
                render: function (item) {
                    const parts = item.place_name.split(',');
                    return `
                        <div class="mapboxgl-ctrl-geocoder--suggestion">
                            <div class="mapboxgl-ctrl-geocoder--suggestion-title">${parts[0]}</div>
                            <div class="mapboxgl-ctrl-geocoder--suggestion-address">${parts.slice(1).join(',')}</div>
                        </div>
                    `;
                }
            });
            map.addControl(geocoder, 'top-left');
        }

        validStores.features.forEach((feature, index) => {
            feature.properties.id = index;
        });

        function flyToStore(feature) {
            map.flyTo({
                center: feature.geometry.coordinates,
                zoom: 15,
                essential: true
            });
        }

        function createPopup(feature) {
            const existingPopups = document.getElementsByClassName('mapboxgl-popup');
            if (existingPopups[0]) existingPopups[0].remove();

            const { name, content, directionsUrl } = feature.properties;

            const directionsLink = directionsUrl
                ? `<a href="${directionsUrl}" target="_blank" rel="noopener" class="store-locator__directions popup-directions">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M21.71 11.29l-9-9a1 1 0 0 0-1.42 0l-9 9a1 1 0 0 0 0 1.42l9 9a1 1 0 0 0 1.42 0l9-9a1 1 0 0 0 0-1.42zM14 14.5V12h-4v3H8v-4a1 1 0 0 1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/></svg>
                     Directions
                   </a>`
                : '';

            new mapboxgl.Popup({ closeOnClick: false, offset: 25 })
                .setLngLat(feature.geometry.coordinates)
                .setHTML(`<h3>${name}</h3><div class="popup-details">${content}${directionsLink}</div>`)
                .addTo(map);
        }

        function setActiveListing(id) {
            const listings = document.querySelectorAll(`#listings${options.sid} .store-locator__item`);
            listings.forEach(listing => listing.classList.remove('is--active'));

            const activeListing = document.getElementById(`listing-${id}`);
            if (activeListing) {
                activeListing.classList.add('is--active');
                activeListing.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }

        function setupListings() {
            const listingsContainer = document.getElementById(`listings${options.sid}`);
            if (!listingsContainer) return;

            const items = listingsContainer.querySelectorAll('.store-locator__item');

            items.forEach((item, index) => {
                const feature = stores.features[index];
                if (!feature) return;

                item.addEventListener('click', (e) => {
                    if (e.target.closest('.store-locator__directions')) return;

                    e.preventDefault();
                    flyToStore(feature);
                    createPopup(feature);
                    setActiveListing(index);
                });
            });
        }

        function addMarkers() {
            const isImgMarker = options.isImgMarker;

            validStores.features.forEach((feature, index) => {
                const marker = document.createElement('div');
                marker.id = `marker-${index}`;
                marker.className = 'store-locator__marker';

                if (!isImgMarker) {
                    marker.innerHTML = `
                        <svg class="store-locator__marker-icon" aria-hidden="true" focusable="false">
                            <use href="#icon--store-locator-marker"></use>
                        </svg>
                    `;
                }

                new mapboxgl.Marker(marker, { offset: [0, -20] })
                    .setLngLat(feature.geometry.coordinates)
                    .addTo(map);

                marker.addEventListener('click', (e) => {
                    e.stopPropagation();
                    flyToStore(feature);
                    createPopup(feature);
                    setActiveListing(index);
                });
            });
        }

        map.on('load', () => {
            map.addSource('places', {
                type: 'geojson',
                data: validStores
            });

            setupListings();
            addMarkers();

            // Initialize Find Nearest Store for Mapbox
            const listings = document.querySelectorAll(`#listings${options.sid} .store-locator__item`);
            initFindNearestMapbox(options.sid, listings, map, validStores);

            // Initialize Search for Mapbox
            initSearchMapbox(options.sid, listings, map, validStores);
        });

        map.on('idle', () => {
            map.resize();
        });

        document.addEventListener('shopify:section:load', (e) => {
            if (e.target.classList.contains('section-store-locator')) {
                map.resize();
            }
        });
    }

    // Initialize when ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(init, 100);
        });
    } else {
        setTimeout(init, 100);
    }
})();
